/**
 * Box 8: what is wrong with the data, what to do, and what it unlocks.
 * Blocking first; dataQualityOf already sorts, and the box keeps that promise
 * for any caller that does not. Below the actions, the period's coverage
 * (spec §9.3): platforms that spent but did not report a metric, missing
 * days, and naming coverage. A failed read says so, never "complete".
 *
 * @module components/looker/app/DataQualityBox
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { platformLabel } from '@/utils/platformLabel';
import type { DataQualityAction } from '@/services/mediaIntelligenceTypes';
import type { CoverageState } from '@/hooks/useLookerCoverage';
import { coverageLinesOf } from '@/services/looker/lookerTileNotes';
import { coverageLabelsFrom } from '@/services/looker/lookerCoverageLabels';
import { BoxMessage } from './appBoxShared';

const SEVERITY_CLASS: Record<DataQualityAction['severity'], string> = {
  blocking: 'text-error-700',
  degraded: 'text-warning-700',
};

const blockingFirst = (actions: readonly DataQualityAction[]): DataQualityAction[] => [
  ...actions.filter((a) => a.severity === 'blocking'),
  ...actions.filter((a) => a.severity !== 'blocking'),
];

const ActionList: React.FC<{ actions: DataQualityAction[] }> = ({ actions }) => {
  const { t } = useTranslation();
  if (actions.length === 0) return <BoxMessage text={t('looker.app.quality.none')} />;
  // Literal keys, so the locale integrity test sees them.
  const severity: Record<DataQualityAction['severity'], string> = {
    blocking: t('looker.app.table.blocking'),
    degraded: t('looker.app.table.degraded'),
  };
  return (
    <ul className="divide-y divide-neutral-100 text-sm">
      {actions.map((a, i) => (
        <li key={`${a.severity}|${a.channel ?? ''}|${i}`} data-quality-item={a.severity} className="min-w-0 py-3">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className={`font-semibold ${SEVERITY_CLASS[a.severity]}`}>{severity[a.severity]}</span>
            {a.channel && <span className="text-neutral-800">{platformLabel(a.channel)}</span>}
          </div>
          <p className="mt-1 break-words font-semibold text-neutral-900">{a.finding}</p>
          <p className="mt-1 break-words text-neutral-800">{a.action}</p>
          <p className="mt-1 break-words text-xs text-neutral-700">{t('looker.app.quality.unlocks', { text: a.unlocks })}</p>
        </li>
      ))}
    </ul>
  );
};

const CoverageSection: React.FC<{ coverage: CoverageState }> = ({ coverage }) => {
  const { t } = useTranslation();
  const lines = coverageLinesOf(coverage, coverageLabelsFrom(t));
  return (
    <section data-coverage={coverage.status} className="mt-2 border-t border-neutral-200 pt-3 text-sm">
      <h4 className="text-xs font-semibold text-neutral-900">{t('looker.coverage.heading')}</h4>
      {coverage.status === 'loading' ? (
        <p role="status" className="mt-1 animate-pulse text-neutral-700">{t('looker.app.loading')}</p>
      ) : (
        <ul className="mt-1 space-y-1">
          {lines.map((l, i) => (
            <li key={`${l.platform ?? ''}|${i}`} className={`break-words ${coverage.status === 'failed' ? 'font-medium text-error-700' : 'text-neutral-800'}`}>
              {l.text}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export const DataQualityBox: React.FC<{ actions: DataQualityAction[]; coverage?: CoverageState }> = ({ actions, coverage }) => (
  <>
    <ActionList actions={blockingFirst(actions)} />
    {coverage && <CoverageSection coverage={coverage} />}
  </>
);
